import axios from "axios";
import {
  SITE_IMAGES_LIST_REQUEST,
  SITE_IMAGES_LIST_SUCCESS,
  SITE_IMAGES_LIST_FAIL,
  SITE_IMAGE_UPSERT_REQUEST,
  SITE_IMAGE_UPSERT_SUCCESS,
  SITE_IMAGE_UPSERT_FAIL,
  SITE_IMAGE_UPDATE_REQUEST,
  SITE_IMAGE_UPDATE_SUCCESS,
  SITE_IMAGE_UPDATE_FAIL,
  SITE_IMAGE_DELETE_REQUEST,
  SITE_IMAGE_DELETE_SUCCESS,
  SITE_IMAGE_DELETE_FAIL,
} from "../constants/siteImageConstants";

// localStorage key used to cache the imagesMap so that on repeat visits
// (e.g. someone reloads the home page) the hero image URL is already
// known on first render — no network roundtrip required before we can
// start downloading the actual image. We still hit /api/site-images in
// the background on every mount so the cache stays fresh; if an admin
// uploads a new image the cache will be replaced on the next page view.
const SITE_IMAGES_CACHE_KEY = "siteImagesCache_v1";

const readSiteImagesCache = () => {
  try {
    const raw = localStorage.getItem(SITE_IMAGES_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const writeSiteImagesCache = (map, queriedKeys = null) => {
  try {
    if (!map) return;
    const prev = readSiteImagesCache();

    let next;
    if (queriedKeys && queriedKeys.length > 0) {
      // Authoritative update for the QUERIED keys: remove them from cache
      // first, then add only what the server returned. Other keys are
      // preserved untouched. This is what fixes the stale-cache bug after
      // an admin deletes a site image — the deleted key won't resurrect
      // on the next page load because we explicitly drop it here when
      // the server stops returning it.
      next = { ...prev };
      for (const k of queriedKeys) delete next[k];
      next = { ...next, ...map };
    } else {
      // Fetched ALL images (no keys filter) → server response is fully
      // authoritative; replace the cache wholesale.
      next = { ...map };
    }

    localStorage.setItem(SITE_IMAGES_CACHE_KEY, JSON.stringify(next));
  } catch {
    // localStorage may be full / disabled (Safari private mode etc.) — no-op.
  }
};

// Surgically remove a single key from the cache (used on delete and from
// SiteImage's onError handler) so a stale Cloudinary URL doesn't get
// replayed from cache on the next page load.
export const removeFromSiteImagesCache = (key) => {
  if (!key) return;
  try {
    const prev = readSiteImagesCache();
    if (key in prev) {
      const next = { ...prev };
      delete next[key];
      localStorage.setItem(SITE_IMAGES_CACHE_KEY, JSON.stringify(next));
    }
  } catch {
    // no-op
  }
};

// GET many by keys
export const fetchSiteImages = (keys = []) => async (dispatch) => {
  try {
    dispatch({ type: SITE_IMAGES_LIST_REQUEST });
    const qs = keys.length ? `?keys=${keys.join(",")}` : "";
    const { data } = await axios.get(`/api/site-images${qs}`);

    // Pass the queried keys through so the reducer can be authoritative
    // for those keys (i.e., delete entries the server no longer returns)
    // instead of just merging — which is what was leaking deleted images
    // into the next page render.
    const queriedKeys = keys.length ? keys : null;
    dispatch({
      type: SITE_IMAGES_LIST_SUCCESS,
      payload: { ...data, queriedKeys },
    });
    // Refresh the persisted cache so the next page load has these URLs ready.
    writeSiteImagesCache(data?.map || {}, queriedKeys);
  } catch (error) {
    dispatch({
      type: SITE_IMAGES_LIST_FAIL,
      payload:
        error?.response?.data?.message || error.message || "Failed to fetch site images",
    });
  }
};

// POST upsert by key (for client-direct Cloudinary flow)
export const upsertSiteImage = (payload) => async (dispatch, getState) => {
  try {
    dispatch({ type: SITE_IMAGE_UPSERT_REQUEST });
    const {
      userLogin: { userInfo },
    } = getState();

    const { data } = await axios.post(`/api/site-images`, payload, {
      headers: { Authorization: `Bearer ${userInfo.token}` },
    });

    dispatch({ type: SITE_IMAGE_UPSERT_SUCCESS, payload: data });
  } catch (error) {
    dispatch({
      type: SITE_IMAGE_UPSERT_FAIL,
      payload:
        error?.response?.data?.message || error.message || "Failed to upsert site image",
    });
  }
};

// PUT update by id
export const updateSiteImage = (id, payload) => async (dispatch, getState) => {
  try {
    dispatch({ type: SITE_IMAGE_UPDATE_REQUEST });
    const {
      userLogin: { userInfo },
    } = getState();

    const { data } = await axios.put(`/api/site-images/${id}`, payload, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userInfo.token}`,
      },
    });

    dispatch({ type: SITE_IMAGE_UPDATE_SUCCESS, payload: data });
  } catch (error) {
    dispatch({
      type: SITE_IMAGE_UPDATE_FAIL,
      payload:
        error?.response?.data?.message || error.message || "Failed to update site image",
    });
  }
};

// DELETE by id
export const deleteSiteImage = (id) => async (dispatch, getState) => {
  try {
    dispatch({ type: SITE_IMAGE_DELETE_REQUEST });
    const {
      userLogin: { userInfo },
      siteImages: { images },
    } = getState();

    // Look up the key BEFORE we hit the server so we can scrub the
    // localStorage cache after a successful delete. Without this step,
    // the deleted Cloudinary URL is replayed from cache on the next page
    // load and the user sees a broken image icon (the local PNG fallback
    // never wins because `record` still exists in the rehydrated state).
    const deleted = (images || []).find((i) => i._id === id);
    const deletedKey = deleted?.key;

    await axios.delete(`/api/site-images/${id}`, {
      headers: { Authorization: `Bearer ${userInfo.token}` },
    });

    if (deletedKey) {
      removeFromSiteImagesCache(deletedKey);
    }

    dispatch({ type: SITE_IMAGE_DELETE_SUCCESS, payload: id });
  } catch (error) {
    dispatch({
      type: SITE_IMAGE_DELETE_FAIL,
      payload:
        error?.response?.data?.message || error.message || "Failed to delete site image",
    });
  }
};
